import React, { useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
  Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';

const PayablesFilters = ({
  filters,
  onFilterChange,
  suppliers,
  categories,
  summaryStats,
  companyId
}) => {
  const theme = useTheme();
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [dialogSelectedVendor, setDialogSelectedVendor] = useState(null);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [dialogSelectedCategory, setDialogSelectedCategory] = useState(null);

  const paymentStatuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'partial', label: 'Partial' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const selectedVendor = suppliers.find(s => s.id === filters.supplierId);
  const filteredVendors = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(vendorSearchQuery.toLowerCase())
  );

  const selectedCategory = categories.find(c => c.value === filters.category);
  const filteredCategories = categories.filter(category =>
    category.label.toLowerCase().includes(categorySearchQuery.toLowerCase())
  );

  const handleOpenVendorDialog = () => {
    setVendorDialogOpen(true);
    setVendorSearchQuery('');
    setDialogSelectedVendor(null);
  };

  const handleCloseVendorDialog = () => {
    setVendorDialogOpen(false);
    setVendorSearchQuery('');
    setDialogSelectedVendor(null);
  };

  const handleDialogVendorSelect = (vendor) => {
    setDialogSelectedVendor(vendor);
  };

  const handleConfirmVendorSelection = () => {
    if (dialogSelectedVendor) {
      onFilterChange('supplierId', dialogSelectedVendor.id);
    } else {
      onFilterChange('supplierId', 'all');
    }
    
    setVendorDialogOpen(false);
    setVendorSearchQuery('');
    setDialogSelectedVendor(null);
  };

  const handleOpenCategoryDialog = () => {
    setCategoryDialogOpen(true);
    setCategorySearchQuery('');
    setDialogSelectedCategory(null);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setCategorySearchQuery('');
    setDialogSelectedCategory(null);
  };

  const handleDialogCategorySelect = (category) => {
    setDialogSelectedCategory(category);
  };

  const handleConfirmCategorySelection = () => {
    if (dialogSelectedCategory) {
      onFilterChange('category', dialogSelectedCategory.value);
    } else {
      onFilterChange('category', 'all');
    }
    
    setCategoryDialogOpen(false);
    setCategorySearchQuery('');
    setDialogSelectedCategory(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ 
        borderRadius: 3, 
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        p: 3,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search invoices, vendors..."
              value={filters.searchQuery}
              onChange={(e) => onFilterChange('searchQuery', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>

          {/* Payment Status */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Payment Status"
              value={filters.paymentStatus}
              onChange={(e) => onFilterChange('paymentStatus', e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            >
              {paymentStatuses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Category - Button to open dialog */}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleOpenCategoryDialog}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderRadius: 2,
                py: 0.8,
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: selectedCategory ? 'text.primary' : 'text.secondary',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {selectedCategory ? selectedCategory.label : 'All Categories'}
            </Button>
          </Grid>

          {/* Vendor - Button to open dialog */}
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={handleOpenVendorDialog}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderRadius: 2,
                py: 0.8,
                borderColor: 'rgba(0, 0, 0, 0.23)',
                color: selectedVendor ? 'text.primary' : 'text.secondary',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {selectedVendor ? selectedVendor.name : 'All Vendors'}
            </Button>
          </Grid>

          {/* Due in X Days */}
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              type="number"
              label="Due in Days"
              value={filters.dueInDays || ''}
              onChange={(e) => onFilterChange('dueInDays', e.target.value)}
              size="small"
              placeholder="All"
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>

          {/* Date From */}
          <Grid item xs={12} sm={6} md={1.25}>
            <DatePicker
              label="From Date"
              value={filters.fromDate ? dayjs(filters.fromDate) : null}
              onChange={(newValue) => onFilterChange('fromDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }
                }
              }}
            />
          </Grid>

          {/* Date To */}
          <Grid item xs={12} sm={6} md={1.25}>
            <DatePicker
              label="To Date"
              value={filters.toDate ? dayjs(filters.toDate) : null}
              onChange={(newValue) => onFilterChange('toDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Summary Chips */}
        {summaryStats && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
            <Chip 
              label={`Total Outstanding: ${formatCurrency(summaryStats.totalOutstanding)}`}
              color="error"
              variant="outlined"
              size="small"
            />
            <Chip 
              label={`Overdue: ${summaryStats.overdueCount} (${formatCurrency(summaryStats.overdueAmount)})`}
              color="warning"
              variant="outlined"
              size="small"
            />
            <Chip 
              label={`Due Next 30 Days: ${formatCurrency(summaryStats.dueNext30Days)}`}
              color="info"
              variant="outlined"
              size="small"
            />
            <Chip 
              label={`Total Paid: ${formatCurrency(summaryStats.totalPaid)}`}
              color="success"
              variant="outlined"
              size="small"
            />
          </Stack>
        )}
      </Paper>

      {/* Vendor Selection Dialog */}
      <Dialog
        open={vendorDialogOpen}
        onClose={handleCloseVendorDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '70vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Filter by Vendor
          </Typography>
          <IconButton onClick={handleCloseVendorDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search vendors..."
              value={vendorSearchQuery}
              onChange={(e) => setVendorSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow 
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2,
                      pl: 3,
                      pr: 3
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        All Vendors
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show invoices from all vendors
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDialogVendorSelect(null)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedVendor === null ? 'black' : 'transparent',
                        color: dialogSelectedVendor === null ? 'white' : 'inherit',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                          bgcolor: dialogSelectedVendor === null ? 'black' : 'transparent'
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
                {filteredVendors.map((vendor) => (
                  <TableRow 
                    key={vendor.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {vendor.name}
                        </Typography>
                        {vendor.contact_email && (
                          <Typography variant="caption" color="text.secondary">
                            {vendor.contact_email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogVendorSelect(vendor)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedVendor?.id === vendor.id ? 'black' : 'transparent',
                          color: dialogSelectedVendor?.id === vendor.id ? 'white' : 'inherit',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: dialogSelectedVendor?.id === vendor.id ? 'black' : 'transparent'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredVendors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {vendorSearchQuery ? 'No vendors found matching your search.' : 'No vendors available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => window.open(`/company/${companyId}/vendors`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Vendor
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseVendorDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmVendorSelection}
              variant="contained"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Category Selection Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={handleCloseCategoryDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '70vh',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Filter by Category
          </Typography>
          <IconButton onClick={handleCloseCategoryDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search categories..."
              value={categorySearchQuery}
              onChange={(e) => setCategorySearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow 
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2,
                      pl: 3,
                      pr: 3
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                        All Categories
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Show all payables
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDialogCategorySelect(null)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedCategory === null ? 'black' : 'transparent',
                        color: dialogSelectedCategory === null ? 'white' : 'inherit',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                          bgcolor: dialogSelectedCategory === null ? 'black' : 'transparent'
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
                {filteredCategories.map((category) => (
                  <TableRow 
                    key={category.value}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {category.label}
                        </Typography>
                        {category.type && (
                          <Typography variant="caption" color="text.secondary">
                            {category.type}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogCategorySelect(category)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedCategory?.value === category.value ? 'black' : 'transparent',
                          color: dialogSelectedCategory?.value === category.value ? 'white' : 'inherit',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            bgcolor: dialogSelectedCategory?.value === category.value ? 'black' : 'transparent'
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCategories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {categorySearchQuery ? 'No categories found matching your search.' : 'No categories available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={handleCloseCategoryDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                border: `1px solid ${theme.palette.grey[600]}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCategorySelection}
            variant="contained"
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default PayablesFilters;
